import React, { Suspense, lazy, useEffect } from 'react';
import {
    BrowserRouter,
    Navigate,
    Outlet,
    Route,
    Routes,
    Link,
    useLocation,
} from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import Header from './components/Header';
import Footer from './components/Footer';
import { useDocumentHead } from './lib/use-document-head';

const NotFound = () => (
    <div className="empty-state">
        <div className="empty-state__inner">
            <h1 className="text-section-hero">404</h1>
            <p className="text-body-large">
                This page does not exist in the current AvenBox site structure.
            </p>
            <Link to="/" className="btn-pill btn-primary">
                Back to AvenBox
            </Link>
        </div>
    </div>
);

const ScrollToTop = () => {
    const { pathname, hash } = useLocation();

    useEffect(() => {
        if (hash) {
            const targetId = hash.replace('#', '');
            window.requestAnimationFrame(() => {
                const target = document.getElementById(targetId);
                if (target) {
                    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    return;
                }
                window.scrollTo(0, 0);
            });
            return;
        }

        window.scrollTo(0, 0);
    }, [pathname, hash]);

    return null;
};

const HeadSync = () => {
    useDocumentHead();
    return null;
};

const HomePage = lazy(() => import('./pages/HomePage'));
const AvenBoxPage = lazy(() => import('./pages/AvenBoxPage'));
const TechnologyPage = lazy(() => import('./pages/TechnologyPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const UseCasesPage = lazy(() => import('./pages/UseCasesPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const NewsPage = lazy(() => import('./pages/NewsPage'));
const NewsArticlePage = lazy(() => import('./pages/NewsArticlePage'));

const Layout = () => (
    <div className="site-shell">
        <Header />
        <main className="site-main">
            <Suspense
                fallback={
                    <div className="loading-state">
                        <div className="loading-spinner" />
                    </div>
                }
            >
                <Outlet />
            </Suspense>
        </main>
        <Footer />
    </div>
);

const App: React.FC = () => {
    return (
        <BrowserRouter>
            <Analytics />
            <ScrollToTop />
            <HeadSync />
            <Routes>
                <Route element={<Layout />}>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/avenbox" element={<AvenBoxPage />} />
                    <Route path="/technology" element={<TechnologyPage />} />
                    <Route path="/security" element={<SecurityPage />} />
                    <Route path="/use-cases" element={<UseCasesPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/news" element={<NewsPage />} />
                    <Route path="/news/:slug" element={<NewsArticlePage />} />
                    <Route path="/solutions" element={<Navigate replace to="/technology" />} />
                    <Route path="/about-us" element={<Navigate replace to="/about" />} />
                    <Route path="/projects" element={<Navigate replace to="/avenbox" />} />
                    <Route path="/model" element={<Navigate replace to="/avenbox" />} />
                    <Route path="*" element={<NotFound />} />
                </Route>
            </Routes>
        </BrowserRouter>
    );
};

export default App;
